import { useMemo, useState } from 'react';
import { useSnackbar } from 'notistack';
import { useContactLists } from '@/api/contact-list/contact-list.hooks';
import {
  useLinkContactList,
  useUnlinkContactList,
} from '@/api/sourcing/sourcing.hooks';
import type { ContactList } from '@/api/contact-list/contact-list.types';
import { DsButton, DsModal, DsSearchInput } from '../../ds';

interface Props {
  tenderId: string;
  /** Currently linked Verteiler IDs. */
  linkedIds: number[];
  onClose: () => void;
}

/**
 * Modal picker for linking/unlinking supplier-side Verteiler to the tender.
 * The user's customer-kind lists are filtered out — a sourcing tender only
 * makes sense against suppliers. Each row is a toggleable add/remove action
 * against the backend; the modal stays open so the user can adjust several
 * Verteiler in one go.
 */
export function VerteilerPicker({ tenderId, linkedIds, onClose }: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const { data: lists = [], isPending } = useContactLists();
  const link = useLinkContactList();
  const unlink = useUnlinkContactList();
  const [query, setQuery] = useState('');

  const linkedSet = useMemo(() => new Set(linkedIds), [linkedIds]);

  const supplierLists = useMemo(
    () => lists.filter((l) => l.kind === 'supplier'),
    [lists],
  );

  const filtered = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (!term) return supplierLists;
    return supplierLists.filter(
      (l) =>
        l.name.toLowerCase().includes(term) ||
        (l.description?.toLowerCase().includes(term) ?? false),
    );
  }, [supplierLists, query]);

  const toggle = (list: ContactList) => {
    const isLinked = linkedSet.has(list.id);
    if (isLinked) {
      unlink.mutate(
        { id: tenderId, listId: list.id },
        {
          onSuccess: () =>
            enqueueSnackbar(`Verteiler "${list.name}" entfernt.`, {
              variant: 'success',
            }),
          onError: () =>
            enqueueSnackbar('Verteiler konnte nicht entfernt werden.', {
              variant: 'error',
            }),
        },
      );
    } else {
      link.mutate(
        { id: tenderId, listId: list.id },
        {
          onSuccess: () =>
            enqueueSnackbar(`Verteiler "${list.name}" hinzugefügt.`, {
              variant: 'success',
            }),
          onError: () =>
            enqueueSnackbar('Verteiler konnte nicht hinzugefügt werden.', {
              variant: 'error',
            }),
        },
      );
    }
  };

  const pending = link.isPending || unlink.isPending;

  return (
    <DsModal
      open
      onClose={onClose}
      title="Verteiler auswählen"
      description="Wähle einen oder mehrere Verteiler, deren Ansprechpartner als Lieferanten zur Ausschreibung hinzugefügt werden."
      footer={<DsButton onClick={onClose}>Fertig</DsButton>}
    >
      <div className="sc-vt-picker">
        <DsSearchInput
          value={query}
          onSearch={setQuery}
          debounce={0}
          placeholder="Verteiler suchen…"
        />
        {isPending ? (
          <p className="sc-hint">Lädt…</p>
        ) : supplierLists.length === 0 ? (
          <p className="sc-hint">
            Keine Lieferanten-Verteiler vorhanden. Lege im Bereich „Verteiler"
            einen Lieferanten-Verteiler an.
          </p>
        ) : filtered.length === 0 ? (
          <p className="sc-hint">Keine Treffer.</p>
        ) : (
          <ul className="sc-vt-list">
            {filtered.map((list) => {
              const isLinked = linkedSet.has(list.id);
              return (
                <li key={list.id} className="sc-vt-row">
                  <label className="sc-vt-row-main">
                    <input
                      type="checkbox"
                      checked={isLinked}
                      disabled={pending}
                      onChange={() => toggle(list)}
                    />
                    <div className="sc-vt-row-text">
                      <div className="sc-vt-row-name">{list.name}</div>
                      {list.description && (
                        <div className="sc-vt-row-desc">{list.description}</div>
                      )}
                    </div>
                  </label>
                  <span className="sc-vt-row-count">
                    {list.members.length}{' '}
                    {list.members.length === 1 ? 'Kontakt' : 'Kontakte'}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </DsModal>
  );
}
